import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { omit } from '../../utils/props';
import { hasCurrencyCode, hasPurchaseRequest } from '../../utils/purchaseMotionTypeGuards';
import { ErrorBoundary, ErrorBoundaryNames } from '../ErrorBoundary';
import { OptionalDFCProvider } from './OptionalDFCProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const AssignSeatButton = Loadable({
  loader: () => import('../PurchaseMotions/AssignSeatButton/AssignSeatButton').then(m => m.AssignSeatButton)
});
const BuyingButton = Loadable({
  loader: () => import('../PurchaseMotions/BuyingButton/BuyingButton').then(m => m.BuyingButton)
});
const PricingPageRedirectButton = Loadable({
  loader: () => import('../PurchaseMotions/PricingPageRedirectButton/PricingPageRedirectButton').then(m => m.PricingPageRedirectButton)
});
const RequestProductButton = Loadable({
  loader: () => import('../PurchaseMotions/RequestProductButton/RequestProductButton').then(m => m.RequestProductButton)
});
const RequestSeatButton = Loadable({
  loader: () => import('../PurchaseMotions/RequestSeatButton/RequestSeatButton').then(m => m.RequestSeatButton)
});
const TalkToSalesButton = Loadable({
  loader: () => import('../PurchaseMotions/TalkToSalesButton/TalkToSalesButton').then(m => m.TalkToSalesButton)
});
const TrialButton = Loadable({
  loader: () => import('../PurchaseMotions/TrialButton/TrialButton').then(m => m.TrialButton)
});
const ViewAccountButton = Loadable({
  loader: () => import('../PurchaseMotions/ViewAccountButton/ViewAccountButton').then(m => m.ViewAccountButton)
});
export const PurchaseMotionCTABase = ({
  apiName,
  ctaProps,
  purchaseMotion,
  uiButtonProps = {},
  trackingData,
  onClick,
  upgradeConfig,
  children,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  isUpgradePoint
}) => {
  //default to primary if not specified
  uiButtonProps = Object.assign({
    use: 'primary'
  }, uiButtonProps);
  const filteredUiButtonProps = omit(uiButtonProps, ['children', 'className', 'external', 'href', 'onClick', 'style']);
  switch (purchaseMotion) {
    case 'ASSIGN_SEAT':
      return /*#__PURE__*/_jsx(AssignSeatButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    case 'BUY_ADDITIONAL_CONTACTS':
    case 'BUY_ADDITIONAL_SEATS':
    case 'BUY_TOUCHLESS':
      if (hasPurchaseRequest(ctaProps)) {
        return /*#__PURE__*/_jsx(BuyingButton, Object.assign({
          purchaseMotion: purchaseMotion,
          apiName: apiName,
          trackingData: trackingData,
          upgradeConfig: upgradeConfig,
          "data-test-id": dataTestId,
          "data-selenium-id": dataSeleniumId,
          uiButtonProps: filteredUiButtonProps,
          onClick: onClick,
          isUpgradePoint: isUpgradePoint
        }, ctaProps, {
          children: children
        }));
      } else if (hasCurrencyCode(ctaProps)) {
        return /*#__PURE__*/_jsx(BuyingButton, Object.assign({
          purchaseMotion: purchaseMotion,
          apiName: apiName,
          trackingData: trackingData,
          upgradeConfig: upgradeConfig,
          "data-test-id": dataTestId,
          "data-selenium-id": dataSeleniumId,
          uiButtonProps: filteredUiButtonProps,
          onClick: onClick,
          isUpgradePoint: isUpgradePoint
        }, ctaProps, {
          children: children
        }));
      } else {
        throw new Error('buying button props must have either purchaseRequest or currencyCode');
      }
    case 'PRICING_PAGE_REDIRECT':
      return /*#__PURE__*/_jsx(PricingPageRedirectButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'REQUEST_SEAT':
      return /*#__PURE__*/_jsx(RequestSeatButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'REQUEST_PRODUCT':
      return /*#__PURE__*/_jsx(RequestProductButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'TALK_TO_SALES':
      return /*#__PURE__*/_jsx(TalkToSalesButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isAssignable: true,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'TALK_TO_SALES_FALLBACK':
      return /*#__PURE__*/_jsx(TalkToSalesButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isAssignable: false,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'TRIAL':
      return /*#__PURE__*/_jsx(TrialButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'VIEW_ACCOUNT':
      return /*#__PURE__*/_jsx(ViewAccountButton, Object.assign({
        apiName: apiName,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    default:
      return null;
  }
};
export const PurchaseMotionCTA = props => {
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: ErrorBoundaryNames.PURCHASE_MOTION_CTA,
    children: /*#__PURE__*/_jsx(OptionalDFCProvider, {
      children: /*#__PURE__*/_jsx(PurchaseMotionCTABase, Object.assign({}, props))
    })
  });
};